import { useState } from "react";
import { Check, Globe } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLanguage } from "@/components/language-provider";
import { cn } from "@/lib/utils";

interface LanguageSwitcherProps {
  /** `header` renders light-on-navy for the dark site header. */
  variant?: "header" | "default";
  className?: string;
}

export function LanguageSwitcher({ variant = "header", className }: LanguageSwitcherProps) {
  const { t } = useTranslation();
  const { language, languages, setLanguage } = useLanguage();
  const [open, setOpen] = useState(false);
  const current = languages.find((l) => l.code === language) ?? languages[0];

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          aria-label={t("language.select")}
          // Fallback for environments where the pointer-event trigger doesn't fire
          // (e.g. embedded preview frames): plain clicks still toggle the menu.
          onClick={() => setOpen((v) => !v)}
          className={cn(
            "shrink-0 gap-2 rounded-lg px-2 sm:px-3",
            variant === "header" &&
              "text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground",
            className,
          )}
        >
          <Globe className="size-4" aria-hidden="true" />
          <span className="hidden sm:inline">{current.name}</span>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="min-w-40">
        {languages.map((l) => (
          <DropdownMenuItem
            key={l.code}
            onSelect={() => setLanguage(l.code)}
            dir={l.dir}
            lang={l.code}
            className="flex items-center justify-between gap-3"
            aria-current={l.code === language ? "true" : undefined}
          >
            <span>{l.name}</span>
            {l.code === language && <Check className="size-4 text-accent" aria-hidden="true" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
